import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Download, FileText, Music2, Loader2, Mail } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const BeatsPurchaseSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionId = searchParams.get('session_id');

  const fetchPurchaseData = useCallback(async (sid) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/beats/purchase-success?session_id=${sid}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch purchase data', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessionId) fetchPurchaseData(sessionId);
    else setLoading(false);
  }, [sessionId, fetchPurchaseData]);

  const getLicenseName = (type) => {
    const names = {
      basic_lease: 'Basic Lease',
      premium_lease: 'Premium Lease',
      exclusive_rights: 'Exclusive Rights',
    };
    return names[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || !data.success) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-xl text-[#A3A3A3] mb-4">Purchase not found</p>
          <Link
            to="/beats"
            className="px-6 py-3 bg-[#D4AF37] text-black font-medium hover:bg-[#E5C048] transition-colors"
          >
            Back to Beats
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-32" data-testid="beats-purchase-success-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        {/* Success Header */}
        <div className="text-center mb-12">
          <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
          <h1 className="font-['Bebas_Neue'] text-5xl md:text-6xl mb-4">PURCHASE SUCCESSFUL!</h1>
          <p className="text-lg text-[#A3A3A3]">Thank you for licensing with IAMHETHATIS</p>
        </div>

        {/* Email confirmation */}
        {data.email_sent && data.email_to && (
          <div
            className="flex items-center gap-3 bg-green-900/20 border border-green-700/40 rounded-lg p-4 mb-8"
            data-testid="email-sent-banner"
          >
            <Mail size={20} className="text-green-400 flex-shrink-0" />
            <p className="text-sm text-green-200">
              Your licensing agreement{data.licensing_agreements?.length > 1 ? 's were' : ' was'} emailed to{' '}
              <span className="font-medium">{data.email_to}</span> as <code>.txt</code> attachment
              {data.licensing_agreements?.length > 1 ? 's' : ''}.
            </p>
          </div>
        )}

        {/* Purchased Beats */}
        <div className="space-y-6 mb-12">
          {data.licensing_agreements?.map((item, index) => (
            <BeatPurchaseRow
              key={item.transaction_id || index}
              item={item}
              sessionId={sessionId}
              getLicenseName={getLicenseName}
            />
          ))}
        </div>

        {/* Important Notice */}
        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-6 mb-8">
          <h3 className="font-medium text-[#D4AF37] mb-2">Important Notice</h3>
          <ul className="text-sm text-[#A3A3A3] space-y-2">
            <li>• Save your licensing agreement(s) for your records</li>
            {data.email_to ? (
              <li>• A copy was emailed to <span className="text-[#D4AF37]">{data.email_to}</span></li>
            ) : (
              <li>• Use the buttons below to download your beat audio and contract</li>
            )}
            <li>• Read the full terms to understand your usage rights</li>
            <li>• For exclusive rights purchases, the beat will be removed from the store within 24 hours</li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/beats"
            className="px-8 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors text-center"
          >
            Browse More Beats
          </Link>
          <Link
            to="/"
            className="px-8 py-3 border border-[#D4AF37] text-[#D4AF37] font-medium uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors text-center"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

const BeatPurchaseRow = ({ item, sessionId, getLicenseName }) => {
  const [downloadingBeat, setDownloadingBeat] = useState(false);
  const [downloadingContract, setDownloadingContract] = useState(false);

  const triggerBlobDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const filenameFromHeaders = (headers, fallback) => {
    const cd = headers['content-disposition'] || headers['Content-Disposition'];
    if (!cd) return fallback;
    const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
    const asciiMatch = cd.match(/filename="?([^";]+)"?/i);
    if (utf8Match) {
      try { return decodeURIComponent(utf8Match[1]); } catch (_) { /* keep fallback */ }
    }
    if (asciiMatch) return asciiMatch[1];
    return fallback;
  };

  const handleDownloadBeat = async () => {
    if (downloadingBeat || !item.beat_id || !sessionId) return;
    setDownloadingBeat(true);
    try {
      const response = await axios.get(
        `${API}/api/beats/${item.beat_id}/download?session_id=${encodeURIComponent(sessionId)}`,
        { responseType: 'blob' }
      );
      const filename = filenameFromHeaders(
        response.headers,
        `IAMHETHATIS - ${item.beat_title} (${getLicenseName(item.license_type)}).mp3`
      );
      triggerBlobDownload(
        new Blob([response.data], { type: response.data.type || 'audio/mpeg' }),
        filename
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Download failed';
      alert(`Beat download failed: ${msg}`);
    } finally {
      setDownloadingBeat(false);
    }
  };

  const handleDownloadContract = async () => {
    if (downloadingContract) return;
    setDownloadingContract(true);
    try {
      // Prefer server-rendered .txt with proper Content-Disposition
      if (item.transaction_id) {
        const response = await axios.get(
          `${API}/api/beats/contract/${item.transaction_id}/download`,
          { responseType: 'blob' }
        );
        const filename = filenameFromHeaders(
          response.headers,
          `IAMHETHATIS - ${item.beat_title} - ${getLicenseName(item.license_type)} Agreement.txt`
        );
        triggerBlobDownload(
          new Blob([response.data], { type: 'text/plain' }),
          filename
        );
      } else {
        // Fallback to client-side .txt from inline agreement text
        const blob = new Blob([item.agreement], { type: 'text/plain' });
        triggerBlobDownload(
          blob,
          `IAMHETHATIS - ${item.beat_title} - ${getLicenseName(item.license_type)} Agreement.txt`
        );
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Download failed';
      alert(`Contract download failed: ${msg}`);
    } finally {
      setDownloadingContract(false);
    }
  };

  return (
    <div
      className="bg-[#1A1A1A] rounded-lg p-6 border border-[#D4AF37]/20"
      data-testid={`beat-purchase-${item.beat_id || item.transaction_id}`}
    >
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-12 h-12 bg-[#D4AF37]/10 rounded flex items-center justify-center flex-shrink-0">
            <Music2 className="text-[#D4AF37]" size={24} />
          </div>
          <div className="min-w-0">
            <h3
              className="font-['Bebas_Neue'] text-2xl text-white truncate select-text cursor-text"
              style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
            >
              {item.beat_title}
            </h3>
            <p className="text-sm text-[#D4AF37] uppercase tracking-wider">
              {getLicenseName(item.license_type)}
            </p>
          </div>
        </div>
        <p className="font-medium text-xl text-[#D4AF37] flex-shrink-0">
          ${item.amount?.toFixed(2)}
        </p>
      </div>

      {/* Licensing Agreement preview */}
      <div className="bg-[#0D0D0D] rounded p-4 border border-[#262626]">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={18} className="text-[#D4AF37]" />
          <p className="font-medium text-white">Licensing Agreement</p>
        </div>
        <pre className="text-xs text-[#A3A3A3] whitespace-pre-wrap font-mono leading-relaxed max-h-64 overflow-y-auto">
          {item.agreement}
        </pre>
      </div>

      {/* Download buttons */}
      <div className="mt-4 flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleDownloadBeat}
          disabled={downloadingBeat || !item.beat_id}
          className="px-4 py-2 bg-[#D4AF37] text-black font-medium text-sm uppercase tracking-wider hover:bg-[#E5C048] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid={`download-beat-${item.beat_id || item.transaction_id}`}
        >
          {downloadingBeat ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {downloadingBeat ? 'Downloading...' : 'Download Beat'}
        </button>
        <button
          onClick={handleDownloadContract}
          disabled={downloadingContract}
          className="px-4 py-2 border border-[#D4AF37] text-[#D4AF37] font-medium text-sm uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid={`download-contract-${item.transaction_id || item.beat_id}`}
        >
          {downloadingContract ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
          Download Contract
        </button>
      </div>
    </div>
  );
};

export default BeatsPurchaseSuccessPage;
